import React, {Component, createRef} from 'react';
import {Text} from 'react-konva';
import {KonvaEventObject} from 'konva/lib/Node';
import {TextElement as TextElementData} from '../../../views/LabelBuilder/LabelBuilder.slice';
import Konva from 'konva';
import { getTextLayout } from '../../../utils/labelText.util';

interface Props {
    element: TextElementData;
    onClick?: (event: KonvaEventObject<MouseEvent>) => void;
    onTransform?: (event: KonvaEventObject<MouseEvent>) => void;
    onDrag?: (event: KonvaEventObject<DragEvent>) => void;
    onRightClick?: (event: KonvaEventObject<PointerEvent>) => void;
    onTextUpdate?: (id: string, textNode: Konva.Text, newText: string) => void;
    draggable?: boolean;
    stageWidth?: number;
}

export default class TextElement extends Component<Props> {
    textRef = createRef<any>();

    handleDblClick = () => {
        const textNode = this.textRef.current;
        if (!textNode) return;

        const stage = textNode.getStage();
        if (!stage) return;

        const {x, y} = textNode.getAbsolutePosition();
        const container = stage.container();

        const textarea = this.createTextarea(textNode, {
            x: container.offsetLeft + x,
            y: container.offsetTop + y,
        });

        textarea.addEventListener('input', (e) => {
            textNode.text((e.target as HTMLTextAreaElement).value);
        });

        textarea.addEventListener('blur', () => this.removeTextarea(textarea, textNode));
        textarea.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
                this.removeTextarea(textarea, textNode);
            }
        });

        textNode.hide();
        document.body.appendChild(textarea);
        textarea.focus();
    };

    createTextarea(textNode: any, position: { x: number; y: number }): HTMLTextAreaElement {
        const textarea = document.createElement('textarea');
        textarea.value = this.props.element.text;
        Object.assign(textarea.style, this.getTextareaStyle(textNode, position));
        return textarea;
    }

    getTextareaStyle(textNode: any, position: { x: number; y: number }): React.CSSProperties {
        const rotation = textNode.rotation();
        const isFirefox = /firefox/i.test(navigator.userAgent);
        const offset = isFirefox ? 2 + Math.round(textNode.fontSize() / 20) : 0;

        return {
            position: 'absolute',
            top: `${position.y}px`,
            left: `${position.x}px`,
            width: `${textNode.width() - textNode.padding() * 2}px`,
            height: `${textNode.height() - textNode.padding() * 2 + 5}px`,
            fontSize: `${textNode.fontSize()}px`,
            fontFamily: textNode.fontFamily(),
            lineHeight: `${textNode.lineHeight()}`,
            textAlign: textNode.align(),
            color: textNode.fill(),
            background: 'none',
            border: 'none',
            padding: '0',
            margin: '0',
            overflow: 'hidden',
            outline: 'none',
            resize: 'none',
            transform: `rotateZ(${rotation}deg) translateY(-${offset}px)`,
            zIndex: 1000,
        };
    }

    removeTextarea(textarea: HTMLTextAreaElement, textNode: any) {
        const newText = textarea.value;

        textNode.text(newText);
        textNode.show();
        textarea.remove();

        if (textNode.getStage()) {
            textNode.getStage().container().focus();
        }

        if (this.props.onTextUpdate) {
            this.props.onTextUpdate(this.props.element.id, textNode, newText);
        }
    }

    render() {
        const {element, draggable = true, stageWidth} = this.props;

        // ✅ KEY FIX: Always compute text width from the stage width.
        // This ensures right-aligned and centered text render correctly on
        // reload, regardless of how the width was originally saved.
        // If stageWidth is not provided, fall back to the saved element width.
        const stageBasedWidth = stageWidth != null
            ? stageWidth - element.x
            : element.width;
        const layout = getTextLayout({
            text: element.text,
            maxWidth: element.width,
            maxHeight: element.height,
            fontSize: element.font.size,
            fontFamily: element.font.family,
            fontStyle: element.font.style,
            direction: element.direction || 'ltr',
        });
        const textWidth = element.width && element.width > 0
            ? element.width
            : stageBasedWidth;
        const textHeight = element.height && element.height > 0
            ? element.height
            : layout.height;

        return (
            <Text
                ref={this.textRef}
                opacity={element.opacity}
                id={element.id}
                key={element.id}
                draggable={draggable}
                text={layout.displayText}
                x={element.x}
                y={element.y}
                width={textWidth}
                height={textHeight}
                scaleX={element.scaleX}
                scaleY={element.scaleY}
                rotation={element.rotation}
                fontFamily={element.font.family}
                fontSize={element.font.size}
                fontStyle={element.font.style}
                fill={element.font.color}
                align={element.align}
                wrap='none'
                onClick={this.props.onClick}
                onTransform={this.props.onTransform}
                onDragEnd={this.props.onDrag}
                onContextMenu={this.props.onRightClick}
                onDblClick={this.handleDblClick}
            />
        );
    }
}
